import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";

/**
 * Issue #460 — the competition / season / team filter row, extracted from
 * `pages/Statistics.jsx` so the coach's Career snapshot carries "the same
 * filters as per the player profile" by construction rather than by
 * imitation.
 *
 * Presentational only. The OPTION LISTS come from the caller, because the
 * two surfaces source them differently on purpose: the player's pages read
 * `/matches/{seasons,competitions,teams}` for the session player, the
 * coach snapshot reads the same three endpoints with `?player_id=` (both
 * behind the API's `resolveScope`). Persistence differs too — the player's
 * choice outlives the visit (localStorage, issue #55), a coach's resets
 * with the selected player — and a shared component that owned either
 * would impose one page's answer on the other.
 *
 * The testids are shared across surfaces deliberately: `stats-filter-*`
 * means "the career stats filter row" wherever it appears, and each route
 * renders at most one.
 *
 * `order` (PR #980 round 2, filter-order finding) — an opt-in prop, default
 * unchanged: `CoachPlayer.dc.html` draws "All seasons · All competitions ·
 * All teams", the opposite of this bar's own long-standing comp/season/team
 * order (`Statistics.jsx`/`CoachCareerSnapshot.jsx`, both untouched — out
 * of scope per D186.6's sibling ruling). Rather than hardcode a second copy
 * of this bar for one caller's ordering, the three blocks are named and
 * looked up by `order`, which defaults to `["comp", "season", "team"]` —
 * the exact JSX this file always rendered, so every existing caller is
 * byte-identical.
 */
export default function StatsFilterBar({
  competition,
  season,
  team,
  competitionOptions,
  seasonOptions,
  teamOptions,
  onCompetitionChange,
  onSeasonChange,
  onTeamChange,
  order = ["comp", "season", "team"],
}) {
  const blocks = {
    comp: (
      <Select key="comp" value={competition} onValueChange={onCompetitionChange}>
        <SelectTrigger className="w-56" data-testid="stats-filter-comp"><SelectValue /></SelectTrigger>
        <SelectContent>
          <SelectItem value="all">All competitions</SelectItem>
          {competitionOptions.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
        </SelectContent>
      </Select>
    ),
    season: (
      <Select key="season" value={season} onValueChange={onSeasonChange}>
        <SelectTrigger className="w-44" data-testid="stats-filter-season"><SelectValue /></SelectTrigger>
        <SelectContent>
          <SelectItem value="all">All seasons</SelectItem>
          {seasonOptions.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
        </SelectContent>
      </Select>
    ),
    team: (
      <Select key="team" value={team} onValueChange={onTeamChange}>
        <SelectTrigger className="w-44" data-testid="stats-filter-team"><SelectValue /></SelectTrigger>
        <SelectContent>
          <SelectItem value="all">All teams</SelectItem>
          {teamOptions.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
        </SelectContent>
      </Select>
    ),
  };

  return (
    <div className="flex gap-3 flex-wrap">
      {order.map((key) => blocks[key])}
    </div>
  );
}
